import { useEffect, useMemo, useState } from 'react';

import {
  ChakraButton,
  ChakraFlex,
  ChakraSimpleGrid,
  ChakraSpacer,
  ChakraVStack,
  Edit,
  EquipUIFireflyV2Theme,
  FAIconName,
  Trash,
  formatCellPhoneNumber,
} from '@equip.health/ui';

import ConfirmModal from '~/components/common/ConfirmModal';
import MessageModal from '~/components/common/MessageModal';
import TitleAndValue from '~/components/common/TitleAndValue';
import { ApiCommand } from '~/lib/Api';
import { PatientCoverage } from '~/lib/constants/patients.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useApi } from '~/lib/hooks';
import { getFormattedDateFromString } from '~/lib/util/patient.util';
import { getPatientCoveragePriorityTextByValue } from '~/lib/util/patientCoverage.util';
import usePatientCoveragesDispatch from '~/store/hooks/patientCoverages';
import { useGetPatientBillingPeriodsQuery } from '~/store/services/clinicalData';

import PatientAddCoverage from './PatientAddCoverage';

const COVERAGE_DELETE_ERROR_TITLE = 'Unable to delete coverage';
const COVERAGE_DELETE_ERROR_SUBTITLE =
  'The coverage you’ve selected is associated with an active billing period and cannot be deleted. To delete this coverage, first remove it from the associated billing period.';
interface PatientCoverageProps {
  coverage: PatientCoverage;
  hasPatientDashboardPermission: boolean;
  onAdd: () => void;
  patientBasicDetails: Patient;
  patientId: string;
  patientCoverages: PatientCoverage[];
}
const { critical, primary } = EquipUIFireflyV2Theme.colors;

const { deletePatientCoverageById } = urlConstants.clinical_data;

const ViewPatientCoverage = ({ coverage }: { coverage: PatientCoverage }) => {
  const {
    alternativeId,
    customPayorName,
    coveragePriority,
    payor,
    isActive,
    groupId,
    providerServicesPhoneNumber,
    stateCode,
    subscriberId,
    patientRelationToSubscriber,
    startDate,
    subscriberFirstName,
    subscriberLastName,
    subscriberDateOfBirth,
    endDate,
  } = coverage;

  const IS_NOT_SELF =
    patientRelationToSubscriber?.toLowerCase() !== 'self' &&
    patientRelationToSubscriber;
  const OTHER_INSURANCE_IDENTIFIER = 'other';

  return (
    <ChakraSimpleGrid columns={3} paddingY="24px" spacing="36px">
      <ChakraVStack alignItems="flex-start" spacing={4}>
        <TitleAndValue title="Insurance company" value={payor} />
        {payor?.toLowerCase() === OTHER_INSURANCE_IDENTIFIER && (
          <TitleAndValue
            title="Other insurance company"
            value={customPayorName || '-'}
          />
        )}
        <TitleAndValue title="Member ID" value={subscriberId || '-'} />
        <TitleAndValue title="Group ID" value={groupId || '-'} />
        <TitleAndValue title="Alternative ID" value={alternativeId || '-'} />
      </ChakraVStack>

      <ChakraVStack alignItems="flex-start" spacing={4}>
        <TitleAndValue
          title="Insurance company state"
          value={stateCode || '-'}
        />
        <TitleAndValue
          title="Provider services phone number"
          value={formatCellPhoneNumber(providerServicesPhoneNumber) || '-'}
        />
        <TitleAndValue
          title="Patient relationship to subscriber"
          value={patientRelationToSubscriber || '-'}
        />
        {IS_NOT_SELF && (
          <TitleAndValue
            title="Subscriber first name"
            value={subscriberFirstName || '-'}
          />
        )}
        {IS_NOT_SELF && (
          <TitleAndValue
            title="Subscriber last name"
            value={subscriberLastName || '-'}
          />
        )}
        {IS_NOT_SELF && (
          <TitleAndValue
            title="Subscriber DOB"
            value={getFormattedDateFromString(subscriberDateOfBirth)}
          />
        )}
      </ChakraVStack>
      <ChakraVStack alignItems="flex-start" spacing={4}>
        <TitleAndValue
          title="Status"
          value={isActive ? 'Active' : 'Inactive'}
        />
        <TitleAndValue
          title="Priority"
          value={getPatientCoveragePriorityTextByValue(coveragePriority) || '-'}
        />
        <TitleAndValue
          title="Start Date"
          value={getFormattedDateFromString(startDate)}
        />
        <TitleAndValue
          title="End Date"
          value={getFormattedDateFromString(endDate)}
        />
      </ChakraVStack>
    </ChakraSimpleGrid>
  );
};

const PatientCoverageDetails = ({
  coverage,
  hasPatientDashboardPermission,
  onAdd,
  patientBasicDetails,
  patientId,
  patientCoverages,
}: PatientCoverageProps) => {
  const { currentPatientId } = useGlobalContext();
  const { data: billingPeriods } =
    useGetPatientBillingPeriodsQuery(currentPatientId);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [openDeleteCoverageModal, setOpenDeleteCoverageModal] = useState(false);
  const [openErrorDeletingCoverageModal, setOpenErrorDeletingCoverageModal] =
    useState(false);

  const {
    error: deletePatientCoverageError,
    sendRequest: deletePatientCoverage,
    statusCode: deletePatientCoverageStatusCode,
  } = useApi<PatientCoverage>();

  const { removePatientCoverage } = usePatientCoveragesDispatch();

  const handleCoverageEdit = () => {
    setIsEditMode(false);
    onAdd();
  };

  const billingPeriodCoverageIds = useMemo(() => {
    const billingPeriodCoverageExternalIds = [];
    (billingPeriods?.data ?? []).forEach((billingPeriod) => {
      billingPeriodCoverageExternalIds.push(billingPeriod.coverageExternalId);
    });
    return billingPeriodCoverageExternalIds;
  }, [billingPeriods?.data]);

  const deleteCoverage = () => {
    if (billingPeriodCoverageIds.includes(coverage.coverageExternalId)) {
      setOpenErrorDeletingCoverageModal(true);
    } else {
      setOpenDeleteCoverageModal(true);
    }
  };

  useEffect(() => {
    if (deletePatientCoverageStatusCode === 200) {
      setOpenDeleteCoverageModal(false);
      onAdd();
      removePatientCoverage(coverage.coverageExternalId, patientId);
    } else if (deletePatientCoverageError) {
      console.error(
        'Error deleting patient contact: ',
        deletePatientCoverageError,
      );
    }
  }, [deletePatientCoverageStatusCode, deletePatientCoverageError]);

  const onDeleteConfirm = () => {
    deletePatientCoverage({
      command: ApiCommand.DELETE,
      url: deletePatientCoverageById(coverage.coverageExternalId),
    });
  };

  return (
    <>
      {hasPatientDashboardPermission && isEditMode ? (
        <PatientAddCoverage
          coverage={coverage}
          onAdd={handleCoverageEdit}
          onDiscard={() => setIsEditMode(false)}
          patientBasicDetails={patientBasicDetails}
          patientCoverages={patientCoverages}
          patientId={patientId}
        />
      ) : (
        <ViewPatientCoverage coverage={coverage} />
      )}
      <MessageModal
        id="coverage-delete-error__modal"
        isOpen={openErrorDeletingCoverageModal}
        onCancel={() => setOpenErrorDeletingCoverageModal(false)}
        subTitle={COVERAGE_DELETE_ERROR_SUBTITLE}
        title={COVERAGE_DELETE_ERROR_TITLE}
      />
      <ConfirmModal
        body="Are you sure you want to delete this coverage? This action cannot be undone."
        cancelIcon={FAIconName.XmarkRegular}
        cancelLabel="Cancel"
        confirmIcon={FAIconName.trashRegular}
        confirmLabel="Delete"
        confirmVariant="accent.critical"
        isOpen={openDeleteCoverageModal}
        onClose={() => setOpenDeleteCoverageModal(false)}
        onConfirm={onDeleteConfirm}
        title="Delete coverage"
      />
      {!isEditMode && (
        <ChakraFlex marginTop="10px">
          <ChakraButton
            _active={{
              backgroundColor: 'transparent',
              boxShadow: 'none',
            }}
            _hover={{ background: 'transparent' }}
            background="transparent"
            border="1px"
            borderColor="critical.base"
            borderRadius="8px"
            fontSize="12px"
            fontWeight="700"
            id="patient-contact__delete-icon"
            onClick={deleteCoverage}
            size="sm"
          >
            <Trash fill={critical.base} height="16px" width="16px" />
          </ChakraButton>
          <ChakraSpacer />
          <ChakraButton
            _active={{
              backgroundColor: 'transparent',
              boxShadow: 'none',
            }}
            _hover={{ background: 'transparent' }}
            background="transparent"
            border="1px"
            borderColor={primary[100]}
            borderRadius="8px"
            fontSize="12px"
            fontWeight="700"
            id="patient-contact__edit-icon"
            onClick={() => setIsEditMode(true)}
            size="sm"
          >
            <Edit fill={primary[100]} height="16px" width="16px" />
          </ChakraButton>
        </ChakraFlex>
      )}
    </>
  );
};

export default PatientCoverageDetails;
